import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { fmtNumber } from '../../utils/region';
import { displayClientName } from '../../utils/displayName';
import OurDocsPicker from '../Jobs/OurDocsPicker';
import DropFolderPicker, { DropSuggest } from '../Jobs/DropFolderPicker';

// ── The email composers' paperclip (report 2454c57a, P0) ─────────────────────────────────────────────────
// "Users to select from their PC, or from our documents, or drop folder." The paperclip of a reply / forward
// (Composer) and of a new email (ComposeNew) opened only the computer's own file window. It opens this small menu
// now: From this computer (that same window — the composer's hidden file input) · From Our Documents · From the
// Drop Folder, the last two being the pickers the Jobs screens already use. A picked file is fetched as it is
// stored (api.filesAsFile), under the name the picker lists it by, and handed to the composer as a file chosen on
// this computer would be: it joins the email's attachments, so Send, Schedule, Undo send and the preview carry it
// like any other, and its × takes it off again. A Drop Folder file is only copied into the email and stays in the
// Drop Folder: a file leaves it only when it is picked for a job.
// Paste and drag into the editor are the composer's own, unchanged.
// The Drop Folder's list is served by the Jobs routes (active_jobs / closed_jobs), so without either module the
// choice is not offered: a refused call pages the admins. Our Documents is open to every staff login.
// `onBusy`: true while a picked file is still on its way, so the composer holds Send until it has arrived.
// The 2nd opening moved the paperclip into a bubble of its own beside AI, with Link and Image (the composers' toolbars),
// and asked: "if the user chooses to select from our documents or drop folder, give them hints in a list based on the
// email information." So both pickers open on whom the email is about (`email`, see lookUpHints below): Our Documents
// with that name in its search box, whose hits are the list, and the Drop Folder with the files named for the job
// number or the name listed first, "Suggested for this email", above its full list.
type Source = 'computer' | 'ours' | 'drop';

/** The email being written, as far as the hints go: its subject, its To, and the email it answers or forwards. */
export type AttachEmail = { subject: string; to: string[]; from?: { name: string; email: string } };
// `words`: the name's words the pickers search by; `fromJob`: they are the client of the subject's job.
type Hints = { words: string[]; jobNo: number; fromJob: boolean };
const NO_HINTS: Hints = { words: [], jobNo: 0, fromJob: false };
const HINT_WAIT = 3000;   // ms a chosen picker waits for them at most

// The job number a subject carries, as the firm's job emails write it — "Job ID: 12,345", "Job #12,345" (server
// services/jobsEmail.ts, jobEmailThreads.ts) — or "Job No. 12345"; 0 when there is none. Four figures at least, and
// never the start of a date ("Job 2026-10-30").
const JOB_IN_SUBJECT = /\bjob\s*(?:id|no\.?|number)?\s*[:#]?\s*#?\s*(\d{1,3}(?:,\d{3})+|\d{4,})(?!\d|[-/.]\d)/i;
const jobNoIn = (subject: string): number => Number(((JOB_IN_SUBJECT.exec(String(subject || '')) || [])[1] || '').replace(/,/g, '')) || 0;

// The words of a name the pickers can search by. Our Documents' search wants every word it is given in a file's
// name, so: the first person of a joint name, titles and a company's legal form off (displayClientName), stops and
// single letters dropped, and only the first and last word of a longer name — a file seldom carries a middle name.
// An address in place of a name gives none.
function nameWords(name: string): string[] {
  if (!name || name.includes('@')) return [];
  const first = displayClientName(name).split(/\s+(?:&|and)\s+|\s*[,/&]\s*/i)[0] || '';
  const words = first.split(/[^\p{L}\p{M}\p{N}]+/u).filter(w => w.length >= 2);
  return words.length > 2 ? [words[0], words[words.length - 1]] : words;
}

// Whom the email is about, first found wins:
//   1. the client of the job whose number the subject carries — asked of the Jobs routes, so only with a Jobs module
//      (as the Drop Folder row: a refused call pages the admins);
//   2. a recipient's Contacts card — or, where the reply goes back to the sender of the email being answered, the
//      name that email came with;
//   3. the sender of the email being answered or forwarded.
// The user's own mailboxes are never taken for the client. The job number is a Drop Folder hint whether or not its job
// is found. Nothing found leaves both pickers as they opened before.
async function lookUpHints(mail: AttachEmail, own: Set<string>, withJobs: boolean): Promise<Hints> {
  const jobNo = jobNoIn(mail.subject);
  const addrOf = (a: string) => String((/<([^>]+)>/.exec(a || '') || [])[1] || a || '').trim().toLowerCase();
  if (jobNo && withJobs) {
    try {
      const d: any = await api.pickJobs({ q: String(jobNo) });
      const job = (Array.isArray(d?.rows) ? d.rows : []).find((r: any) => Number(r?.job_no) === jobNo);
      const words = nameWords(String(job?.person || ''));
      if (words.length) return { words, jobNo, fromJob: true };
    } catch { /* no job to go by — the email's people next */ }
  }
  const from = mail.from && !own.has(addrOf(mail.from.email)) ? mail.from : null;
  const to = (mail.to || []).map(addrOf).filter(a => a && !own.has(a)).slice(0, 3);
  for (const addr of to) {
    let words: string[] = [];
    try {
      const cards: any = await api.searchContacts(addr);
      const card = (Array.isArray(cards) ? cards : []).find((c: any) => String(c?.email || '').toLowerCase().split(/[\s,;]+/).includes(addr));
      words = nameWords(String(card?.name || ''));
    } catch { /* no card to go by */ }
    if (!words.length && from && addrOf(from.email) === addr) words = nameWords(from.name);
    if (words.length) return { words, jobNo, fromJob: false };
  }
  return { words: from ? nameWords(from.name) : [], jobNo, fromJob: false };
}

export default function AttachFileMenu({ title, onComputer, onFile, onBusy, email }: {
  title: string; onComputer: () => void; onFile: (file: File) => void; onBusy?: (busy: boolean) => void; email?: AttachEmail;
}) {
  const { t } = useTranslation();
  const showToast = useStore(s => s.showToast);
  const mods = useStore(s => s.me?.modules) || [];
  const accounts = useStore(s => s.accounts);
  const canDrop = mods.includes('active_jobs') || mods.includes('closed_jobs');
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState<'' | 'ours' | 'drop'>('');
  const [busy, setBusy] = useState(false);
  const [hints, setHints] = useState<Hints>(NO_HINTS);
  const [looking, setLooking] = useState(false);   // a picker was chosen and waits for the hints
  const hintRun = useRef<{ key: string; p: Promise<Hints> } | null>(null);
  const inFlight = useRef(0);
  const alive = useRef(true);
  const boxRef = useRef<HTMLSpanElement>(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  // Looked up when the menu opens, so the hints are usually there by the time a picker is chosen; asked again only
  // once the subject's job number or the people on the email have changed.
  const hintsNow = (): Promise<Hints> => {
    if (!email) return Promise.resolve(NO_HINTS);
    const key = JSON.stringify([jobNoIn(email.subject), email.to || [], email.from?.email || '', email.from?.name || '']);
    if (hintRun.current?.key !== key) {
      const own = new Set(accounts.map(a => String(a.email || '').trim().toLowerCase()).filter(Boolean));
      hintRun.current = { key, p: lookUpHints(email, own, canDrop).catch(() => NO_HINTS) };
    }
    return hintRun.current.p;
  };
  const choose = (s: Source) => {
    setOpen(false);
    if (s === 'computer') { onComputer(); return; }
    setLooking(true);
    // A lookup that has not answered within HINT_WAIT opens the picker without hints rather than not at all.
    const late = new Promise<Hints>(done => setTimeout(() => done(NO_HINTS), HINT_WAIT));
    Promise.race([hintsNow(), late]).then(h => {
      if (!alive.current) return;
      setLooking(false); setHints(h); setPicker(s);
    });
  };
  // The picker closes on a pick; the file joins the attachments once it has been fetched. A composer closed
  // meanwhile is left alone.
  const take = (id: string, name: string, mime: string) => {
    setPicker('');
    inFlight.current += 1; setBusy(true); onBusy?.(true);
    api.filesAsFile(id, name, mime)
      .then(file => { if (alive.current) onFile(file); })
      .catch((e: any) => { if (alive.current) showToast(t('eml_attach_failed', { name, error: e?.message || '' })); })
      .finally(() => {
        inFlight.current -= 1;
        if (!inFlight.current && alive.current) { setBusy(false); onBusy?.(false); }
      });
  };

  const rows: { s: Source; icon: string; label: string }[] = [
    { s: 'computer', icon: 'ti-upload', label: t('eml_attach_pc') },
    { s: 'ours', icon: 'ti-folder', label: t('eml_attach_ours') },
    ...(canDrop ? [{ s: 'drop' as Source, icon: 'ti-folder-open', label: t('eml_attach_drop') }] : []),
  ];
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left', whiteSpace: 'nowrap' };
  const hover = { onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'var(--bg-active)'), onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'transparent') };
  // What the pickers open with: the name in Our Documents' search box, and the Drop Folder's suggestions. Each hint
  // line says where its starting point came from; with nothing found they read as they did before.
  const seed = hints.words.join(' ');
  const oursHint = !seed ? t('eml_attach_ours_hint')
    : hints.fromJob ? t('eml_attach_ours_hint_job', { name: seed, job: fmtNumber(hints.jobNo) })
    : t('eml_attach_ours_hint_name', { name: seed });
  const suggest: DropSuggest | undefined = hints.jobNo || hints.words.length ? { jobNo: hints.jobNo, words: hints.words } : undefined;
  const spin = busy || looking;

  return (
    <span ref={boxRef} style={{ position: 'relative', display: 'flex' }}>
      {/* Drawn as Link and Image beside it in the bubble are (the composers' TBtn, 20px high): pressed on mouse-down,
          so the editor keeps its cursor. */}
      <button type="button" onMouseDown={e => { e.preventDefault(); if (!open) hintsNow(); setOpen(!open); }} title={busy ? t('eml_attach_busy') : title} aria-label={title} aria-haspopup="menu" aria-expanded={open}
        style={{ width: 26, height: 20, borderRadius: 'var(--radius-sm)', border: 'none', background: open ? 'var(--c-gray-100)' : 'transparent', color: open ? 'var(--text-primary)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <i className={`ti ${spin ? 'ti-loader-2' : 'ti-paperclip'}`} style={{ fontSize: 13, ...(spin ? { animation: 'spin 1s linear infinite' } : {}) }} aria-hidden />
      </button>
      {open && (
        <div role="menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 6, zIndex: 60 }}>
          {rows.map(r => (
            <button key={r.s} type="button" role="menuitem" onClick={() => choose(r.s)} style={rowStyle} {...hover}>
              <i className={`ti ${r.icon}`} style={{ fontSize: 13, color: 'var(--text-tertiary)', width: 14 }} aria-hidden />
              <span>{r.label}</span>
            </button>
          ))}
        </div>
      )}
      {picker === 'ours' && <OurDocsPicker docName="" seed={seed} hint={oursHint} onPick={f => take(f.id, f.name, f.mime)} onClose={() => setPicker('')} />}
      {picker === 'drop' && <DropFolderPicker docName="" suggest={suggest} hint={t(suggest ? 'eml_attach_drop_hint_seed' : 'eml_attach_drop_hint')} onPick={f => take(f.id, f.name, f.mime)} onClose={() => setPicker('')} />}
    </span>
  );
}
